import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import PreInscricaoCard from '../../components/PreInscricoes/PreInscricaoCard';
import { listarPreInscricoes } from '../../services/preInscricaoService';
import { CSVLink } from 'react-csv';
import debounce from 'lodash.debounce';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ListaPreInscricoesPage = () => {
  const navigate = useNavigate();
  const [preInscricoes, setPreInscricoes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Função debounced para otimizar a busca
  const debouncedFetch = React.useRef(
    debounce((page, limit, search, status) => {
      fetchPreInscricoes(page, limit, search, status);
    }, 500)
  ).current;

  useEffect(() => {
    debouncedFetch(currentPage, itemsPerPage, searchTerm, statusFilter);
    return () => {
      debouncedFetch.cancel();
    };
  }, [currentPage, itemsPerPage, searchTerm, statusFilter, debouncedFetch]);

  const fetchPreInscricoes = async (page, limit, search, status) => {
    setLoading(true);
    try {
      const data = await listarPreInscricoes(page, limit, search, status);
      setPreInscricoes(data.preInscricoes);
      setTotalPages(data.totalPages);
      setError('');
    } catch (error) {
      console.error('Erro ao buscar pré-inscrições:', error);
      setError(error.response?.data?.mensagem || 'Erro ao buscar pré-inscrições');
      setSnackbar({
        open: true,
        message: error.response?.data?.mensagem || 'Erro ao buscar pré-inscrições',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const headers = [
    { label: "ID", key: "id" },
    { label: "Nome do Estudante", key: "estudante.nome" },
    { label: "Email", key: "estudante.email" },
    { label: "Campanha", key: "campanha.nome" },
    { label: "Status", key: "status" },
    { label: "Data da Pré-inscrição", key: "createdAt" },
  ];

  const csvData = preInscricoes.map((preInscricao) => ({
    id: preInscricao.id,
    "estudante.nome": preInscricao.estudante.nome,
    "estudante.email": preInscricao.estudante.email,
    "campanha.nome": preInscricao.campanha.nome,
    status: preInscricao.status,
    createdAt: new Date(preInscricao.createdAt).toLocaleDateString(),
  }));

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Pré-Inscrições
      </Typography>
      <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Button
          component={Link}
          to="/pre-inscricoes/criar"
          variant="contained"
          color="primary"
        >
          Criar Nova Pré-Inscrição
        </Button>
        <Button
          onClick={handleBackToDashboard}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          Voltar ao Dashboard
        </Button>
        <CSVLink data={csvData} headers={headers} filename={"preinscricoes.csv"} style={{ textDecoration: 'none' }}>
          <Button variant="contained" color="success">
            Exportar como CSV
          </Button>
        </CSVLink>
      </Box>
      {/* Campos de Busca e Filtro */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <TextField
          label="Buscar por Nome"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ minWidth: '200px' }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: '150px' }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status"
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            <MenuItem value="Pendente">Pendente</MenuItem>
            <MenuItem value="Aprovada">Aprovada</MenuItem>
            <MenuItem value="Rejeitada">Rejeitada</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {error && (
        <Typography variant="body1" color="error" gutterBottom>
          {error}
        </Typography>
      )}
      <Grid container spacing={3}>
        {loading ? (
          <Grid item xs={12} display="flex" justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : preInscricoes.length > 0 ? (
          preInscricoes.map((preInscricao) => (
            <Grid item key={preInscricao.id} xs={12} sm={6} md={4}>
              <PreInscricaoCard preInscricao={preInscricao} />
            </Grid>
          ))
        ) : (
          <Typography variant="body1">Nenhuma pré-inscrição encontrada.</Typography>
        )}
      </Grid>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
        <FormControl variant="outlined" size="small">
          <InputLabel id="items-per-page-label">Itens por Página</InputLabel>
          <Select
            labelId="items-per-page-label"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            label="Itens por Página"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          variant="outlined"
          shape="rounded"
        />
      </Box>
      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListaPreInscricoesPage;